import apiClient from './apiClient';
import type { AuthUser } from '../stores/authStore';

// Typed wrappers for the BP 26 student-identity endpoints
// (backend/src/routes/studentAuth.js + routes/teach.js).

export interface StudentLoginResult {
  token: string;
  user: AuthUser;
  must_change_password: boolean;
}

// POST /api/v1/auth/student — student-ID + password login.
export async function studentLogin(
  student_id: string,
  password: string,
  device_id: string
): Promise<StudentLoginResult> {
  const { data } = await apiClient.post<StudentLoginResult>('/api/v1/auth/student', {
    student_id,
    password,
    device_id,
  });
  return data;
}

// POST /api/v1/auth/password — the one route a must_change_password account
// can reach (requireAuthAllowPasswordChange).
export async function changePassword(
  current_password: string,
  new_password: string
): Promise<{ user: AuthUser }> {
  const { data } = await apiClient.post<{ user: AuthUser }>('/api/v1/auth/password', {
    current_password,
    new_password,
  });
  return data;
}

// POST /api/v1/auth/reset/request — always resolves; no enumeration.
// `email_configured` (WIP-002) is a global "is any provider active right
// now" flag, not a per-student answer — the endpoint always 202s regardless,
// so this adds no enumeration signal. It exists so the UI can stop implying
// an email is on its way when nothing is actually configured to send one.
export async function requestReset(student_id: string): Promise<{ email_configured: boolean }> {
  const { data } = await apiClient.post<{ email_configured?: boolean }>('/api/v1/auth/reset/request', { student_id });
  return { email_configured: !!data?.email_configured };
}

// POST /api/v1/auth/reset/confirm — works for either channel (emailed code or
// instructor-issued code) — same shape.
export async function confirmReset(
  student_id: string,
  code: string,
  new_password: string
): Promise<void> {
  await apiClient.post('/api/v1/auth/reset/confirm', { student_id, code, new_password });
}

// POST /api/v1/teach/students/:userId/reset-code — instructor-mediated reset,
// the DEFAULT flow. Requires an instructor session.
export async function issueResetCode(
  userId: string
): Promise<{ code: string; expires_at: string }> {
  const { data } = await apiClient.post<{ code: string; expires_at: string }>(
    `/api/v1/teach/students/${userId}/reset-code`
  );
  return data;
}

export interface AccessCodeResult {
  token: string;
  user: AuthUser;
  guest: true;
}

/**
 * POST /api/v1/auth/access-code — the third way in.
 *
 * One shared code, one shared read-only guest session. Nothing is collected
 * about who typed it, and nothing the session does can be saved.
 */
export async function accessCodeLogin(
  code: string, nickname?: string,
): Promise<AccessCodeResult> {
  const { data } = await apiClient.post<AccessCodeResult>('/api/v1/auth/access-code', {
    code, nickname: nickname || '',
  });
  return data;
}
